import React, { useMemo, useRef, useState } from "react";
import { usePanZoom } from "./usePanZoom";

function edgePath(from, to) {
  const x1 = from.x + from.width;
  const y1 = from.y + from.height / 2;
  const x2 = to.x;
  const y2 = to.y + to.height / 2;
  const dx = Math.max(40, (x2 - x1) * 0.5);
  return `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
}

function nodeFill(type, selected) {
  if (selected) return "#FFF3EA";
  if (type === "root") return "#FF781F";
  return "#000000";
}

function nodeStroke(type, selected) {
  if (selected) return "#FF781F";
  if (type === "root") return "#FF781F";
  return "#E5E7EB";
}

function textColor(type) {
  if (type === "root") return "#FFFFFF";
  return "#ffffff";
}

export default function TreeHierarchyChart({
  layout,
  onNodeClick,
  selectedNodeId,
  onNodeSelect,
  viewMode = "scroll",
}) {
  const containerRef = useRef(null);
  const [selectedId, setSelectedId] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const activeSelectedId = selectedNodeId ?? selectedId;

  const nodeById = useMemo(() => {
    const m = new Map();
    (layout?.nodes || []).forEach((n) => m.set(n.id, n));
    return m;
  }, [layout]);

  const {
    transform,
    setPan,
    svgTransform,
    handlers,
    onWheel,
    zoomIn,
    zoomOut,
    reset,
  } = usePanZoom({ initialX: 24, initialY: 24, initialScale: 1, minScale: 0.35, maxScale: 2.8 });

  const scrollRef = useRef(null);

  React.useEffect(() => {
    if (viewMode === "scroll" && scrollRef.current) {
      const targetX = -transform.x;
      const targetY = -transform.y;
      if (Math.abs(scrollRef.current.scrollLeft - targetX) > 1) {
        scrollRef.current.scrollLeft = targetX;
      }
      if (Math.abs(scrollRef.current.scrollTop - targetY) > 1) {
        scrollRef.current.scrollTop = targetY;
      }
    }
  }, [transform.x, transform.y, viewMode]);

  const handleScroll = (e) => {
    if (viewMode === "scroll") {
      const newX = -e.currentTarget.scrollLeft;
      const newY = -e.currentTarget.scrollTop;
      if (Math.abs(transform.x - newX) > 1 || Math.abs(transform.y - newY) > 1) {
        setPan(newX, newY);
      }
    }
  };

  const getRect = () => containerRef.current?.getBoundingClientRect?.() || null;

  const handleWheel = (e) => {
    if (viewMode === "scroll") return;
    const rect = getRect();
    if (!rect) return;
    onWheel(e, rect);
  };

  const handleZoomIn = () => {
    const rect = getRect();
    if (!rect) return;
    zoomIn(rect);
  };

  const handleZoomOut = () => {
    const rect = getRect();
    if (!rect) return;
    zoomOut(rect);
  };

  const handleNodeClick = (node) => {
    if (typeof onNodeSelect === "function") onNodeSelect(node.id);
    else setSelectedId(node.id);
    if (typeof onNodeClick === "function") onNodeClick(node.payload);
  };

  const dragHandlers = {
    onPointerDown: (e) => {
      setIsPanning(true);
      handlers.onPointerDown(e);
    },
    onPointerMove: handlers.onPointerMove,
    onPointerUp: (e) => {
      setIsPanning(false);
      handlers.onPointerUp(e);
    },
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full h-[70vh] min-h-[520px] bg-white rounded-md border border-gray-200 overflow-hidden"
      style={{ userSelect: "none", WebkitUserSelect: "none" }}
    >
      {(viewMode === "panning" || viewMode === "scroll") && (
        <div className="absolute top-3 right-3 z-20 flex items-center gap-2">
          <button className="px-3 py-1 rounded border border-gray-200 bg-white text-sm" onClick={handleZoomIn} type="button">
            +
          </button>
          <button className="px-3 py-1 rounded border border-gray-200 bg-white text-sm" onClick={handleZoomOut} type="button">
            −
          </button>
          <button className="px-3 py-1 rounded border border-gray-200 bg-white text-sm" onClick={reset} type="button">
            Reset
          </button>
        </div>
      )}

      <div
        ref={scrollRef}
        className={`w-full h-full ${viewMode === "scroll" ? "overflow-auto" : "overflow-hidden"}`}
        onScroll={handleScroll}
      >
        <svg
          className={viewMode === "scroll" ? "" : "w-full h-full"}
          width={viewMode === "scroll" ? (layout?.bounds?.width || 0) * transform.scale + 48 : undefined}
          height={viewMode === "scroll" ? (layout?.bounds?.height || 0) * transform.scale + 48 : undefined}
          onWheel={handleWheel}
          style={{ cursor: viewMode === "panning" ? (isPanning ? "grabbing" : "grab") : "default" }}
        >
          {viewMode === "panning" && (
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              fill="transparent"
              pointerEvents="all"
              {...dragHandlers}
            />
          )}
          <g transform={viewMode === "scroll" ? `scale(${transform.scale}) translate(24 24)` : svgTransform}>
            {(layout?.edges || []).map((e) => {
              const from = nodeById.get(e.from);
              const to = nodeById.get(e.to);
              if (!from || !to) return null;
              return <path key={e.id} d={edgePath(from, to)} fill="none" stroke="#CBD5E1" strokeWidth="2" />;
            })}

            {(layout?.nodes || []).map((n) => {
              const selected = activeSelectedId === n.id;
              return (
                <g
                  key={n.id}
                  onClick={() => handleNodeClick(n)}
                  onPointerDown={(e) => e.stopPropagation()}
                  style={{ cursor: "pointer" }}
                >
                  <rect
                    x={n.x}
                    y={n.y}
                    width={n.width}
                    height={n.height}
                    rx="12"
                    fill={nodeFill(n.type, selected)}
                    stroke={nodeStroke(n.type, selected)}
                    strokeWidth={n.type === "root" ? 2 : 1}
                  />
                  <text
                    x={n.x + 12}
                    y={n.y + 28}
                    fill={textColor(n.type)}
                    fontSize="13"
                    fontFamily="ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto"
                  >
                    {String(n.label || "")}
                  </text>
                  <text
                    x={n.x + 12}
                    y={n.y + 52}
                    fill={textColor(n.type)}
                    fontSize="18"
                    fontWeight="700"
                    fontFamily="ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto"
                  >
                    {String(n.value ?? "")}
                  </text>
                  {(n.type === "employee") && (
                  <>
                    <text
                      x={n.x + n.width - 10}
                      y={n.y + 20}
                      fill="#fff"
                      fontSize="15"
                      textAnchor="end"
                    >
                      WS: {n.withSubmissions ?? 0}
                    </text>

                    <text
                      x={n.x + n.width - 10}
                      y={n.y + 44}
                      fill="#fff"
                      fontSize="15"
                      textAnchor="end"
                    >
                      WoS: {n.withoutSubmissions ?? 0}
                    </text>
                  </>
                )}
              </g>
              );
            })}
          </g>
        </svg>
      </div>
    </div>
  );
}
